'use client';

import type { Browser } from '@payments/aurum-contracts';
import jsSha from 'js-sha3';
import { useWorldpayCardEncryption, type RawCardData, type TEncryptedWorldPayData } from './useWorldpayCardEncryption';
import { useWorldpayCardSessionId } from './useWorldpayCardSessionId';

type Fingerprint2Component = {
  key: string;
  value: string;
};

type Fingerprint2 = {
  get: (callback: (components: Fingerprint2Component[]) => void) => void;
  x64hash128: (values: string, seed: number) => string;
};

type Fingerprint2Module = {
  default: Fingerprint2;
} & Fingerprint2;

/**
 * Provides all the methods needed to create a deposit transaction with a Worldpay card
 * @returns the methods needed to create a deposit transaction with a Worldpay card
 */
const useWorldpayCard = ({ onError }: { onError?: (error: string) => void } = {}) => {
  const { getEncryptedData, cleanup: cleanupEncryption } = useWorldpayCardEncryption();
  const { getSessionId: getSessionIdFromSessionIdLibrary, cleanup: cleanupSessionId } = useWorldpayCardSessionId();

  const loadFingerprint2 = async (): Promise<Fingerprint2> => {
    const mod = (await import('fingerprintjs2')) as Fingerprint2Module;
    return mod?.default ?? mod;
  };

  /**
   * creates a SHA3_512 hash of the given string
   * @param stringToHash - the string to hash
   * @returns the SHA3_512 hash of the given string
   */
  const hashString = (stringToHash: string): string => {
    return jsSha.sha3_512(stringToHash);
  };

  /**
   * gets the fingerprint of the browser
   * @returns the fingerprint of the browser
   */
  const getFingerprint2 = (): Promise<string> =>
    new Promise(resolve => {
      if (typeof window === 'undefined') {
        resolve('');
        return;
      }

      const run = async () => {
        const Fingerprint2 = await loadFingerprint2();
        const fingerprintGetCallback = (components: Fingerprint2Component[]) => {
          const values = components.map(component => component.value);
          resolve(Fingerprint2.x64hash128(values.join(''), 31));
        };
        Fingerprint2.get(fingerprintGetCallback);
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => void run());
        return;
      }

      setTimeout(() => void run(), 500);
    });

  /**
   * gets the browser data required for worldpay
   * @returns the browser data
   */
  const getBrowserData = async (): Promise<Browser> => ({
    screenWidth: `${window.screen.width}`,
    screenHeight: `${window.screen.height}`,
    screenColorDepth: `${window.screen.colorDepth}`,
    language: `${navigator.language}`,
    javaEnabled: `${navigator.javaEnabled?.() ?? false}`,
    userAgent: `${navigator.userAgent}`,
    timeZone: `${new Date().getTimezoneOffset()}`,
    deviceId: await getFingerprint2(),
  });

  /**
   * encrypts the data using the Worldpay encryption library
   * @param data - the data to encrypt
   * @returns the encrypted data
   */
  const encrypt = async (data: RawCardData): Promise<TEncryptedWorldPayData | null> => {
    try {
      const encData = await getEncryptedData(data);
      return { encData };
    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error.message : String(error));
      }
      return null;
    }
  };

  /**
   * gets the session id from the Worldpay iframe
   * @param data - the data to get the session id from
   * @returns the session id
   */
  const getSessionId = async (data: RawCardData, idempotencyToken: string): Promise<string> => {
    if (!data.cardNumber) return '';
    try {
      return await getSessionIdFromSessionIdLibrary(data.cardNumber, idempotencyToken);
    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error.message : String(error));
      }
      return '';
    }
  };

  /**
   * cleans up all active Worldpay operations
   */
  const cleanup = () => {
    cleanupEncryption();
    cleanupSessionId();
  };

  return {
    hashString,
    getBrowserData,
    encrypt,
    getSessionId,
    cleanup,
  };
};

export { useWorldpayCard };
export type { RawCardData };
